/**
 * PrescienceSkillEffect — Astral oracle-path awareness window with passive death-save.
 *
 * Prescience replaces Oracle (replacesId: oracle) and adds two layers on top of it:
 *
 * Layer 1 (inherited from OracleSkillEffect):
 *   Active awareness window — everything Oracle does:
 *   - 5s window (extendable to 7s via oracle_duration's durationMs bonus)
 *   - showOracleOverlay / hideOracleOverlay via ForesightRenderSystem
 *   - One free Phase Step per window (consumeFreePhaseStep())
 *   - oracle_phase_step invuln override: 500ms during window, restored to 250ms after
 *
 * Layer 2 (passive death-save — always active, independent of the active window):
 *   Once every 20 seconds, when an incoming hit would reduce the player's HP to 0 or below,
 *   Prescience fires a time-slow: global Phaser time scale drops to 0.1 for 300ms.
 *   The fatal hit is absorbed entirely — the player survives with their HP unchanged.
 *   The death-save fires regardless of whether the active component is on cooldown.
 *   The 20-second internal cooldown begins when the death-save fires.
 *
 * Death-save integration contract:
 *   The health system calls `onFatalHit()` before applying a hit that would kill the player.
 *   If `onFatalHit()` returns true: skip damage application (hit is absorbed, HP unchanged).
 *   If it returns false: apply the hit normally (death-save on internal cooldown).
 *
 * Free activation (for Ghost Protocol — astral_projection apotheosis):
 *   `activateForFree()` triggers the Oracle-equivalent active window regardless of cooldown.
 *   Post-window cooldown fires normally after the free window expires.
 *   Returns false if Prescience is already active.
 *
 * Design decision:
 *   Death-save uses global Phaser time scale (scene.time.timeScale = 0.1) — see
 *   design-decisions.md § "Prescience Death-Save: Global Phaser Time Scale". In pure-logic
 *   simulation the `timeController` dependency abstracts the scene's time scale control.
 *   The 300ms restore window is tracked in simulation ticks (unscaled delta) so the harness
 *   can test it without a live Phaser context.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § prescience
 * @see design-decisions.md § Prescience Death-Save: Global Phaser Time Scale
 * @see design-decisions.md § Ghost Protocol Free Prescience: Bypasses Cooldown
 * @see ADR-064 (attunement shared systems architecture)
 */

import { OracleSkillEffect, type OracleDeps, type OracleConfig } from './OracleSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

/** Controls the global time scale — wraps Phaser's scene.time.timeScale in real usage. */
export interface TimeController {
  setTimeScale(scale: number): void;
  getTimeScale(): number;
}

export interface PrescienceDeps extends OracleDeps {
  readonly timeController: TimeController;
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface PrescienceConfig extends OracleConfig {
  /** Internal cooldown between death-save triggers in ms (20000ms default). */
  readonly deathSaveCooldownMs: number;
  /** Time scale applied during the death-save window (0.1 = 10% speed). */
  readonly deathSaveSlowScale: number;
  /** Duration of the death-save slow window in unscaled ms (300ms default). */
  readonly deathSaveWindowMs: number;
}

const DEFAULT_PRESCIENCE_CONFIG: PrescienceConfig = {
  baseWindowMs: 5000,
  cooldownMs: 1200,    // medium cooldown tier
  baseInvulnMs: 250,   // Phase Step base invuln
  deathSaveCooldownMs: 20000,
  deathSaveSlowScale: 0.1,
  deathSaveWindowMs: 300,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class PrescienceSkillEffect extends OracleSkillEffect {
  override readonly skillId = 'prescience';

  protected readonly prescienceDeps: PrescienceDeps;
  protected readonly prescienceConfig: PrescienceConfig;

  /** Remaining ms of the 20-second death-save internal cooldown. */
  private _deathSaveCooldownRemaining = 0;

  /** Remaining ms of the active time-slow restore window. */
  private _deathSaveRestoreTimer = 0;

  /** Total number of death-saves triggered (for testing and stats). */
  private _deathSaveCount = 0;

  constructor(deps: PrescienceDeps, config: Partial<PrescienceConfig> = {}) {
    super(deps, config);
    this.prescienceDeps = deps;
    this.prescienceConfig = { ...DEFAULT_PRESCIENCE_CONFIG, ...config };
  }

  // --- State getters ---

  /** Remaining ms of the death-save internal cooldown (0 = ready). */
  get deathSaveCooldownRemaining(): number { return this._deathSaveCooldownRemaining; }

  /** Total death-saves fired since construction. */
  get deathSaveCount(): number { return this._deathSaveCount; }

  /** True while the time-slow restore window is active. */
  get isDeathSaveSlowing(): boolean { return this._deathSaveRestoreTimer > 0; }

  // ---------------------------------------------------------------------------
  // Free activation (Ghost Protocol integration)
  // ---------------------------------------------------------------------------

  /**
   * Trigger the active Oracle window without consuming or checking the current cooldown.
   *
   * Called by astral_projection (Ghost Protocol apotheosis) to grant a free Prescience
   * window. The activation bypasses the current cooldown state. Post-window cooldown
   * fires normally after the window expires.
   *
   * Returns false if Prescience is already active (a no-op in that case).
   */
  activateForFree(): boolean {
    if (this._isActive) return false;
    const savedCooldown = this.cooldownRemaining;
    this.cooldownRemaining = 0;
    const result = super.activate();
    if (!result) {
      // activate() still failed for some other reason — restore cooldown
      this.cooldownRemaining = savedCooldown;
    }
    return result;
  }

  // ---------------------------------------------------------------------------
  // Death-save passive
  // ---------------------------------------------------------------------------

  /**
   * Call when an incoming hit would reduce the player's HP to 0 or below.
   *
   * If the death-save is available (internal cooldown elapsed):
   *   - Sets time scale to deathSaveSlowScale (0.1) for deathSaveWindowMs (300ms).
   *   - Starts the 20-second internal cooldown.
   *   - Returns true → the caller MUST absorb the hit (player survives, HP unchanged).
   *
   * If the death-save is on internal cooldown:
   *   - Returns false → the caller should apply the hit normally.
   *
   * The death-save fires regardless of whether the active window is running.
   */
  onFatalHit(): boolean {
    if (this._deathSaveCooldownRemaining > 0) return false;

    this._deathSaveCooldownRemaining = this.prescienceConfig.deathSaveCooldownMs;
    this._deathSaveRestoreTimer = this.prescienceConfig.deathSaveWindowMs;
    this._deathSaveCount++;
    this.prescienceDeps.timeController.setTimeScale(this.prescienceConfig.deathSaveSlowScale);
    return true;
  }

  // ---------------------------------------------------------------------------
  // Update override
  // ---------------------------------------------------------------------------

  /**
   * Ticks Oracle's active window, cooldown, and the death-save timers.
   *
   * The death-save internal cooldown and restore window tick on every update,
   * independent of whether the Oracle active window is running.
   */
  override update(delta: number): void {
    // Tick Oracle: active window countdown + activation cooldown.
    super.update(delta);

    // Tick death-save internal cooldown (always, not just while active).
    if (this._deathSaveCooldownRemaining > 0) {
      this._deathSaveCooldownRemaining = Math.max(
        0,
        this._deathSaveCooldownRemaining - delta,
      );
    }

    // Tick death-save restore window. Restore normal time scale when window expires.
    if (this._deathSaveRestoreTimer > 0) {
      this._deathSaveRestoreTimer = Math.max(0, this._deathSaveRestoreTimer - delta);
      if (this._deathSaveRestoreTimer === 0) {
        this.prescienceDeps.timeController.setTimeScale(1.0);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Destroy
  // ---------------------------------------------------------------------------

  override destroy(): void {
    // Ensure time scale is restored if we're mid-slow when destroyed.
    if (this._deathSaveRestoreTimer > 0) {
      this.prescienceDeps.timeController.setTimeScale(1.0);
      this._deathSaveRestoreTimer = 0;
    }
    super.destroy();
  }
}
